// @ts-nocheck
/* eslint-disable spire/avoid-dynamic-translate */
import translate from "@insite/client-framework/Translate";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Select from "@insite/mobius/Select";
import Typography from "@insite/mobius/Typography";
import useReportApi from "@store/Framework/Common/Hooks/useReportAPI/useReportAPI";
import useClear from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useClear";
import useInvoiceSummaryState from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useInvoiceSummaryState";
import useInvoiceSearchFormUpdate from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useUpdateSearchFields";
import { CustomWidgetModule, InvoiceSummaryData, ReportOrderKey } from "@store/Framework/Types/types";
import ApiButtons from "@store/Library/Components/Reporting/Common/ApiButtons";
import { LocationPicker } from "@store/Library/Components/Reporting/LocationPicker/LocationPicker";
import POPicker from "@store/Library/Components/Reporting/POPicker/POPicker";
import React, { useMemo } from "react";
import styled, { css } from "styled-components";

import { invoiceDetailAndSummaryExport } from "@store/Framework/Common/Utilities/excelExportFunctions";
import { InvoiceDetailsModel, InvoiceSummaryModel } from "@store/Framework/Services/Reporting/ReportingModels";
import { isInvoiceDetails, isInvoiceSummaryModel } from "@store/Framework/Services/Reporting/ReportingTypeGuards";
import { reportParametersSelector } from "@store/Framework/Store/Bunzl/Pages/Reporting/ReportOrderFrequency/ReportOrderFrequencySelectors";
import AccordionLayout from "@store/Library/Components/Reporting/Common/AccordionLayout";
import ButtonExportFile from "@store/Library/Components/Reporting/Common/ButtonExportFile";
import ReportContainer from "@store/Library/Components/Reporting/Common/ReportContainer";
import InvoiceSummaryDisplay from "@store/Library/Components/Reporting/InvoiceDetail/InvoiceSummaryDisplay";
import { GridWidths } from "@store/MobiusOverrides/GridItem/GridItem";
import { historyFilterPropsAlt } from "@store/Themes/common/props/history";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { useSelector } from "react-redux";

const mobileQuery = "@media (max-width: 767px)";
const DatePicker = getWidgetComponent("Reports/ReportSearchFieldDateRange");
const gridMaxWidth = {
    css: css`
        max-width: 342px !important;
    `,
};
export interface CustomerInvoiceSummaries {
    [customer: string]: InvoiceSummaryData[];
}

export interface CompanyInvoiceSummaries {
    [company: string]: CompanyInvoiceSummary;
}
export interface CompanyInvoiceSummary {
    locations: CustomerInvoiceSummaries;
    name: string;
}

export const getCompanyInvoiceSummaries = (data: InvoiceSummaryModel): CompanyInvoiceSummaries => {
    const companyInvoiceSummaries: CompanyInvoiceSummaries = {};
    data.items.forEach(invoiceSummary => {
        if (!companyInvoiceSummaries[invoiceSummary.companyid]) {
            companyInvoiceSummaries[invoiceSummary.companyid] = { name: invoiceSummary.companyname, locations: {} };
        }
        if (!companyInvoiceSummaries[invoiceSummary.companyid].locations[invoiceSummary.customerid]) {
            companyInvoiceSummaries[invoiceSummary.companyid].locations[invoiceSummary.customerid] = [];
        }
        companyInvoiceSummaries[invoiceSummary.companyid].locations[invoiceSummary.customerid].push(invoiceSummary);
    });
    return companyInvoiceSummaries;
};
export const ExportButtonGridItem = styled(GridItem)`
    display: flex;
    ${mobileQuery} {
        justify-content: center;
        margin: 1.5rem;
        flex-wrap: wrap;
    }
    justify-content: flex-end;
    align-items: center;
`;
export const searchFormStyles = {
    headerText: { variant: "h3" },
    gridContainer: historyFilterPropsAlt.gridContainer,
    gridItem: { ...historyFilterPropsAlt.gridItem, ...gridMaxWidth },
    gridItemDate: {
        width: [12, 12, 12, 6, 6] as GridWidths[],
        ...gridMaxWidth,
    },
    subGridItem: historyFilterPropsAlt.subGridItem,
    applyButton: historyFilterPropsAlt.applyButton,
};
export const invoiceSummaryHeaders: string[] = [
    "Invoice #",
    "Invoice Date",
    "Order #",
    "PO #",
    "Order SubTotal",
    "Order Tax Summary",
    "Order Freight",
    "Order Total",
];
export const invoiceSummaryKeys: (keyof InvoiceSummaryData)[] = [
    "invoicenum",
    "invoicedate",
    "orderid",
    "ponum",
    "itemstotal",
    "gst",
    "freightcharges",
    "ordertotal",
];

const styles = searchFormStyles;

const InvoiceHistorySearchForm: React.FC = () => {
    const onClear = useClear();
    const [invoiceSummaryState] = useInvoiceSummaryState();
    const {
        updateData: updateInvoices,
        isLoading: isInvoicesLoading,
        hasSubmitted,
        setHasSubmitted,
    } = useReportApi("invoices");
    const [invoiceDetailsData, setInvoiceDetailsData] = React.useState<InvoiceDetailsModel>();
    const { updateData, isLoading, isError } = useReportApi("invoiceSummary");
    const updateInvoiceSearchFields = useInvoiceSearchFormUpdate();
    const parameters = useSelector(reportParametersSelector);
    const { sourceType, billableTreatment, dateType } = parameters;

    const updateStore = (key: ReportOrderKey, value: string) => {
        const newValue = { [key]: value };
        updateInvoiceSearchFields({ ...parameters, ...newValue });
    };
    const handleClear = () => {
        onClear();
        setHasSubmitted(false);
    };

    const handleDataUpdate = async () => {
        const apiReturn = await updateData({ ...parameters });
        if (isInvoiceSummaryModel(apiReturn)) {
            const invoiceNumbers = [...new Set(apiReturn.items.map(invoice => invoice.invoicenum.toString()))];
            const invoiceDetails = await updateInvoices({ ...parameters, invoiceNums: invoiceNumbers });

            if (isInvoiceDetails(invoiceDetails)) {
                setInvoiceDetailsData(invoiceDetails);
            }
        }
    };

    const companyInvoiceSummaries = useMemo(() => {
        return invoiceSummaryState ? getCompanyInvoiceSummaries(invoiceSummaryState) : null;
    }, [invoiceSummaryState]);
    return (
        <ReportContainer>
            <AccordionLayout>
                <GridItem
                    css={css`
                        padding-block: 0.5rem;
                    `}
                    width={12}
                >
                    <GridContainer
                        offsetCss={css`
                            align-items: flex-end;
                        `}
                    >
                        <GridItem {...styles.gridItemDate}>
                            <DatePicker />
                        </GridItem>

                        <GridItem {...styles.gridItemDate}>
                            <Select
                                value={sourceType}
                                error={isError && !sourceType}
                                onChange={event => updateStore("sourceType", event.currentTarget.value)}
                                label={<Typography as="p">{translate("Data Source")}</Typography>}
                            >
                                <option disabled={true} value={0}>
                                    {translate("Select a data source")}
                                </option>
                                <option key={0} value="invoice">
                                    {translate("Invoices")}
                                </option>
                                <option key={1} value="service">
                                    {translate("Services")}
                                </option>
                                <option key={2} value="invoiceservice">
                                    {translate("Invoices and Services")}
                                </option>
                            </Select>
                        </GridItem>
                        <GridItem width={[12, 12, 3, 3, 3, 3]} {...styles.gridItem}>
                            <LocationPicker isError={isError} />
                        </GridItem>
                        <GridItem width={[12, 12, 3, 3, 3, 3]} {...styles.gridItem}>
                            <POPicker />
                        </GridItem>

                        <ApiButtons onClear={handleClear} updateData={handleDataUpdate} isLoading={isLoading} />
                    </GridContainer>
                </GridItem>
            </AccordionLayout>
            {companyInvoiceSummaries && (
                <>
                    <ExportButtonGridItem width={12}>
                        <ButtonExportFile
                            callback={() =>
                                invoiceDetailAndSummaryExport(
                                    invoiceSummaryState?.items || [],
                                    invoiceDetailsData,
                                    companyInvoiceSummaries,
                                )
                            }
                            isLoading={isInvoicesLoading}
                            disabled={!invoiceDetailsData}
                        />
                    </ExportButtonGridItem>
                    <InvoiceSummaryDisplay
                        companyInvoiceSummaries={companyInvoiceSummaries}
                        billableTreatment={billableTreatment}
                        sourceType={sourceType}
                    />
                </>
            )}
            {!invoiceSummaryState?.items?.length && hasSubmitted && (
                <Typography as="p" variant="h3">
                    {translate("No results found")}
                </Typography>
            )}
        </ReportContainer>
    );
};

const widgetModule: CustomWidgetModule = {
    component: InvoiceHistorySearchForm,
    definition: {
        group: "Reporting",
        displayName: "Invoice Summary Report",
    },
};

export default widgetModule;
